// covered-by: frontends/carrie/src/ask-v2/review/__tests__/useAskV2ReviewCommentFilter.test.tsx
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { type ReviewSourceFilterOption } from "./AskV2ReviewTagFilter";
import {
  type CommentWithOptionalTags,
  defaultActiveReviewCommentTags,
  isReviewCommentVisible,
  PUNTT_FINDINGS_SOURCE,
  type ReviewCommentSourceKey,
  type ReviewCommentTagId,
  reviewExpertFilterOptions,
} from "./reviewCommentTags";

import { getUsers } from "../../services/users";

type Options = {
  comments: readonly CommentWithOptionalTags[];
  authorNameByUserId?: Readonly<Record<string, string>>;
};

/**
 * The findings filter for one review: which types are checked, which raisers
 * ("Puntt", or one expert's review) are hidden, and whether a comment passes.
 */
export function useAskV2ReviewCommentFilter({
  comments,
  authorNameByUserId,
}: Options) {
  const { t } = useTranslation("ui");
  const [activeTags, setActiveTags] = useState<Set<ReviewCommentTagId>>(
    defaultActiveReviewCommentTags,
  );
  const [hiddenSources, setHiddenSources] = useState<
    Set<ReviewCommentSourceKey>
  >(() => new Set());

  const expertOptions = useMemo(
    () => reviewExpertFilterOptions(comments, authorNameByUserId),
    [authorNameByUserId, comments],
  );
  // why: an expert's method runs in chats they are not part of -- that is the
  // point of it -- so the chat's participants often cannot name them. Only
  // then is the enterprise roster fetched, under the key its other readers share.
  const needsRoster = expertOptions.some((expert) => !expert.name);
  const roster = useQuery({
    queryKey: ["retrieve-users"],
    queryFn: () => getUsers(),
    enabled: needsRoster,
  });

  const sources = useMemo((): ReviewSourceFilterOption[] => {
    if (expertOptions.length === 0) return [];
    const rosterNames: Record<string, string> = {};
    for (const user of roster.data ?? []) {
      const label = user.name?.trim() || user.email?.trim();
      if (label) rosterNames[user._id] = label;
    }
    const experts = expertOptions.map((expert) => {
      const name = expert.name ?? rosterNames[expert.expertId];
      return {
        key: expert.key,
        label: name
          ? t("askV2.review.sourceExpert", { name })
          : t("askV2.review.sourceExpertUnnamed"),
      };
    });
    return [
      { key: PUNTT_FINDINGS_SOURCE, label: t("askV2.review.sourcePuntt") },
      ...experts,
    ];
  }, [roster.data, t, expertOptions]);

  const toggleTag = useCallback((tag: ReviewCommentTagId, checked: boolean) => {
    setActiveTags((current) => {
      const next = new Set(current);
      if (checked) next.add(tag);
      else next.delete(tag);
      return next;
    });
  }, []);

  const toggleSource = useCallback(
    (source: ReviewCommentSourceKey, checked: boolean) => {
      setHiddenSources((current) => {
        const next = new Set(current);
        if (checked) next.delete(source);
        else next.add(source);
        return next;
      });
    },
    [],
  );

  const isVisible = useCallback(
    (comment: CommentWithOptionalTags) =>
      isReviewCommentVisible(comment, activeTags, hiddenSources),
    [activeTags, hiddenSources],
  );

  // why: a review whose findings carry no tags yet still draws the filter, so
  // a person can narrow once tags arrive (untagged rows stay visible).
  const showTagFilter = comments.some((comment) => !comment.isDeleted);

  return {
    activeTags,
    toggleTag,
    isVisible,
    showTagFilter,
    sourceFilter:
      sources.length > 0
        ? { sources, hidden: hiddenSources, onToggle: toggleSource }
        : undefined,
  };
}
